<script setup lang="ts">
/**
 * MockChatComposer — 通用聊天输入框
 * --------------------------------
 * MockCanvas（OneInputManyOutputsV3 三个模块）与 LayeredFileV3（Swap visuals 演示）
 * 共用同一份 UI：附件 chip + textarea + 左下 add file + 右下 send/spinner。
 *
 * 设计点：
 * - v-model 控制 chatInput；readonly 时禁止编辑但保留 caret 闪烁（CSS caret-color）。
 * - typing 演示场景：父组件把字符串逐字塞进 v-model + readonly=true 即可，无需另外一套假 UI。
 * - loading=true 把 send 按钮替换为 spinner，尺寸保持不变避免布局抖动。
 * - attachment chip 由父组件控制是否可关闭（removable）。
 */

import { ref, watch, nextTick, onMounted } from 'vue';

export interface ChatAttachment {
  name: string;
  size?: string;
  type?: 'doc' | 'pdf' | 'md' | 'image';
}

interface Props {
  /** 文本内容，v-model 双向绑定 */
  modelValue: string;
  /** 占位符文案。设计规范：句首大写、句末省略号 */
  placeholder?: string;
  /** 只读：禁止用户编辑，演示 typing 时使用 */
  readonly?: boolean;
  /** loading：把 send 按钮替换为 spinner */
  loading?: boolean;
  /** 是否显示左下 add file 按钮 */
  showAddFile?: boolean;
  /** 已上传/待发送的附件元数据 */
  attachment?: ChatAttachment | null;
  /** 附件 chip 是否显示 × 关闭按钮（默认 false，演示态不让用户取消） */
  removableAttachment?: boolean;
  /** textarea 是否高度自适应（默认 true）。readonly + 单行场景可关掉 */
  autoResize?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Type something...',
  readonly: false,
  loading: false,
  showAddFile: true,
  attachment: null,
  removableAttachment: false,
  autoResize: true,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'send'): void;
  (e: 'add-file'): void;
  (e: 'remove-attachment'): void;
}>();

const textareaRef = ref<HTMLTextAreaElement | null>(null);

const autoResizeTextarea = () => {
  if (!props.autoResize) return;
  const el = textareaRef.value;
  if (!el) return;
  el.style.height = 'auto';
  if (!props.modelValue) {
    const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight) || 20;
    el.style.height = `${lineHeight}px`;
    return;
  }
  el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
};

watch(
  () => props.modelValue,
  () => {
    void nextTick(autoResizeTextarea);
  }
);

onMounted(() => {
  autoResizeTextarea();
});

const onInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  emit('update:modelValue', target.value);
  autoResizeTextarea();
};

const onEnterKey = (event: KeyboardEvent) => {
  // Shift+Enter 允许换行；纯 Enter 提交
  if (event.shiftKey) return;
  event.preventDefault();
  triggerSend();
};

const triggerSend = () => {
  if (props.loading) return;
  if (!props.modelValue.trim()) return;
  emit('send');
};

defineExpose({
  focus: () => textareaRef.value?.focus(),
});

const textareaWrapMode = () => (props.autoResize ? 'soft' : 'off');

// SVG 文件图标按 type 区分配色
const attachmentColorClass = (type?: ChatAttachment['type']) => {
  switch (type) {
    case 'pdf':
      return 'bg-[#e53935]/10 text-[#e53935]';
    case 'md':
      return 'bg-black/[0.04] text-black/70';
    case 'image':
      return 'bg-[#13a17a]/10 text-[#13a17a]';
    case 'doc':
    default:
      return 'bg-[#1a73e8]/10 text-[#1a73e8]';
  }
};
</script>

<template>
  <div
    class="composer rounded-2xl border border-black/5 bg-white/60 backdrop-blur-[20px] transition-colors hover:border-black/10"
    :class="{ 'composer--readonly': readonly }"
  >
    <!-- 附件 chip 容器：用 CSS Grid `grid-template-rows: 0fr → 1fr` 把高度变化做成丝滑过渡，
         不会因为附件挂载而让 composer 高度瞬变；内层 overflow:hidden 配合裁掉转场中的溢出。
         chip 自身的 opacity + translateY 仍由 Transition 接力做"上滑+淡入"。 -->
    <div class="attachment-slot" :class="{ 'attachment-slot--open': !!attachment }">
      <div class="attachment-slot__inner">
        <Transition name="attachment-chip">
          <div v-if="attachment" class="px-3 pt-2">
            <div
              class="flex w-fit max-w-full items-center gap-2 rounded-xl border border-black/5 bg-white px-2.5 py-1.5"
            >
              <span
                class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                :class="attachmentColorClass(attachment.type)"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                  <path d="M9 13h6" />
                  <path d="M9 17h6" />
                </svg>
              </span>
              <div class="min-w-0 leading-tight">
                <p class="truncate text-[12px] font-medium text-black/80">
                  {{ attachment.name }}
                </p>
                <p v-if="attachment.size" class="text-[10px] text-black/40">
                  {{ attachment.size }}
                </p>
              </div>
              <button
                v-if="removableAttachment"
                type="button"
                class="ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-black/40 transition-colors hover:bg-black/[0.04] hover:text-black/80"
                aria-label="Remove attachment"
                @click="emit('remove-attachment')"
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                >
                  <path d="M3 3L9 9M9 3L3 9" />
                </svg>
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Textarea -->
    <div class="composer__input-row px-3 py-2">
      <textarea
        ref="textareaRef"
        :value="modelValue"
        rows="1"
        :wrap="textareaWrapMode()"
        :placeholder="placeholder"
        :readonly="readonly"
        class="composer__textarea w-full resize-none overflow-hidden bg-transparent text-[13px] leading-[20px] text-black caret-black placeholder:text-black/35 focus:outline-none"
        @input="onInput"
        @keydown.enter="onEnterKey"
      />
    </div>

    <!-- 底部操作行 -->
    <div class="flex items-center justify-between px-3 pb-2">
      <button
        v-if="showAddFile"
        type="button"
        class="flex h-7 w-7 items-center justify-center rounded-full border border-black/10 text-black/50 transition-colors hover:border-black/40 hover:text-black/70 focus-visible:ring-2 focus-visible:ring-black/15 focus-visible:outline-none"
        aria-label="Add file"
        @click="emit('add-file')"
      >
        <svg
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
            d="M12 4v16m8-8H4"
          />
        </svg>
      </button>
      <span v-else aria-hidden="true" />

      <!-- send / spinner 同 8x8 容器，避免布局跳动 -->
      <div class="relative h-8 w-8">
        <Transition name="send-swap">
          <span
            v-if="loading"
            class="absolute inset-0 flex items-center justify-center rounded-full bg-black"
            aria-label="Generating"
          >
            <span
              class="composer__spinner h-3 w-3 rounded-full border-[1.5px] border-white/30 border-t-white"
            />
          </span>
        </Transition>
        <Transition name="send-swap">
          <button
            v-if="!loading"
            type="button"
            class="absolute inset-0 flex items-center justify-center rounded-full bg-black text-white transition-[opacity,transform,background-color] hover:bg-black/85 focus-visible:ring-2 focus-visible:ring-black/15 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!modelValue.trim()"
            aria-label="Send"
            @click="triggerSend"
          >
            <svg
              width="12"
              height="14"
              viewBox="0 0 12 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M5.36899 0.224375C5.76177 -0.0959754 6.34077 -0.0733816 6.70689 0.292734L11.7069 5.29273C12.0971 5.68329 12.0973 6.31639 11.7069 6.7068C11.3165 7.09707 10.6833 7.09694 10.2928 6.7068L6.99985 3.41383V12.4998C6.99972 13.0519 6.552 13.4997 5.99985 13.4998C5.44765 13.4998 4.99999 13.0519 4.99985 12.4998V3.41383L1.70689 6.7068C1.31646 7.09707 0.683328 7.09694 0.292823 6.7068C-0.0976545 6.31632 -0.0975609 5.68327 0.292823 5.29273L5.29282 0.292734L5.36899 0.224375Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </Transition>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* readonly 时仍保留闪烁 caret，让 typing 演示更像真人输入 */
.composer--readonly .composer__textarea {
  cursor: default;
  caret-color: rgba(0, 0, 0, 0.7);
}

/* spinner 旋转 */
.composer__spinner {
  animation: composer-spin 0.9s linear infinite;
}
@keyframes composer-spin {
  to {
    transform: rotate(360deg);
  }
}

/* 附件 chip 容器：用 CSS Grid `grid-template-rows: 0fr → 1fr` 让 composer 高度变化丝滑过渡。
 * - 0fr 时占位为 0；切到 1fr 时高度从 0 平滑增长到子节点真实高度。
 * - inner 必须 overflow:hidden + min-height:0，否则 chip 在转场途中会"溢出"露出来。
 * 与 chip 自身的 attachment-chip transition 错开 60ms：先 expand 让出空间，再淡入 chip，
 * 避免"chip 出现的瞬间挤压 textarea"那种突变感。 */
.attachment-slot {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 320ms cubic-bezier(0.32, 0.72, 0.18, 1);
}
.attachment-slot--open {
  grid-template-rows: 1fr;
}
.attachment-slot__inner {
  min-height: 0;
  overflow: hidden;
}

/* 附件 chip 自身进入/退出：在 slot 撑开过程中做 opacity + 上滑 */
.attachment-chip-enter-active {
  transition:
    opacity 240ms ease 60ms,
    transform 280ms cubic-bezier(0.32, 0.72, 0, 1) 60ms;
}
.attachment-chip-leave-active {
  transition:
    opacity 180ms ease,
    transform 220ms cubic-bezier(0.32, 0.72, 0, 1);
}
.attachment-chip-enter-from,
.attachment-chip-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/* send/spinner cross-fade */
.send-swap-enter-active,
.send-swap-leave-active {
  transition:
    opacity 200ms ease,
    transform 200ms ease;
}
.send-swap-enter-from,
.send-swap-leave-to {
  opacity: 0;
  transform: scale(0.92);
}

@media (prefers-reduced-motion: reduce) {
  .composer__spinner,
  .attachment-slot,
  .attachment-chip-enter-active,
  .attachment-chip-leave-active,
  .send-swap-enter-active,
  .send-swap-leave-active {
    animation: none !important;
    transition: none !important;
  }
}
</style>
